/**
 * Dynatrace Logger Service for Frontend
 * Handles batching, queuing, and sending logs to Dynatrace
 */

export interface DynatraceConfig {
  enabled: boolean;
  url: string;
  apiToken: string;
  serviceName: string;
  ingestEndpoint?: string;
  batchSizeLimit?: number;
  period?: number;
  queueSizeLimit?: number;
  tags?: string;
  minimumLevel?: 'TRACE' | 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';
}

export interface DynatraceLogEntry {
  content: string;
  timestamp: string;
  'log.level': string;
  loglevel: string;
  status: string;
  'service.name': string;
  'trace.id'?: string;
  'exception.type'?: string;
  'exception.message'?: string;
  'exception.stacktrace'?: string;
  tags?: string[];
  [key: string]: any; // Additional properties
}

// Holds the current session trace ID for all log entries
let currentTraceId: string | undefined;

const safeStringify = (value: unknown): string => {
  if (typeof value === 'string') return value;

  const seen = new WeakSet<object>();

  try {
    return JSON.stringify(value, (_key, nestedValue) => {
      if (typeof nestedValue === 'object' && nestedValue !== null) {
        if (seen.has(nestedValue)) return '[Circular]';
        seen.add(nestedValue);
      }

      return nestedValue;
    });
  } catch {
    return String(value);
  }
};

const removeUndefinedValues = (attributes?: Record<string, any>): Record<string, any> => {
  if (!attributes) return {};

  return Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => value !== undefined)
  );
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) return error;

  if (typeof error === 'string') return new Error(error);

  if (error && typeof error === 'object') {
    const errorRecord = error as Record<string, any>;
    const nestedError = errorRecord.error;
    const message =
      errorRecord.message ||
      errorRecord.errorMessage ||
      nestedError?.message ||
      nestedError?.error ||
      nestedError?.data?.message ||
      nestedError?.data?.error ||
      nestedError?.data?.title ||
      safeStringify(error);

    const normalizedError = new Error(String(message));
    normalizedError.name = errorRecord.name || nestedError?.name || 'Error';
    normalizedError.stack = errorRecord.stack || nestedError?.stack || safeStringify(error);

    return normalizedError;
  }

  return new Error(String(error));
};

export function setTraceId(traceId: string): void {
  currentTraceId = traceId;
}

export function getTraceId(): string | undefined {
  return currentTraceId;
}

class DynatraceBatchSender {
  private config: Required<DynatraceConfig>;
  private logQueue: DynatraceLogEntry[] = [];
  private timer: number | null = null;
  private isSending = false;
  private disposed = false;

  constructor(config: DynatraceConfig) {
    this.config = {
      enabled: config.enabled,
      url: config.url,
      apiToken: config.apiToken,
      serviceName: config.serviceName,
      ingestEndpoint: config.ingestEndpoint || '/api/v2/logs/ingest',
      batchSizeLimit: config.batchSizeLimit || 100,
      period: config.period || 2,
      queueSizeLimit: config.queueSizeLimit || 10000,
      tags: config.tags || '',
      minimumLevel: config.minimumLevel || 'INFO',
    };

    if (!this.config.enabled) return;

    if (!this.config.url || !this.config.apiToken) {
      console.error('Dynatrace URL and API token are required when enabled');
      return;
    }

    // Start periodic batch sending
    this.timer = window.setInterval(() => {
      this.sendBatch().catch((err) => {
        console.error('Error in Dynatrace batch timer:', err);
      });
    }, this.config.period * 1000);
  }

  enqueueLog(entry: DynatraceLogEntry): void {
    if (!this.config.enabled || !entry) return;

    // Enforce queue size limit
    if (this.logQueue.length >= this.config.queueSizeLimit) {
      console.warn(`Dynatrace queue limit reached (${this.config.queueSizeLimit}). Dropping oldest log.`);
      this.logQueue.shift();
    }

    this.logQueue.push(entry);

    // Trigger immediate send if batch size reached
    if (this.logQueue.length >= this.config.batchSizeLimit) {
      this.sendBatch().catch((err) => {
        console.error('Error sending Dynatrace batch:', err);
      });
    }
  }

  async sendBatch(): Promise<void> {
    if (this.disposed || this.logQueue.length === 0 || this.isSending) return;

    this.isSending = true;

    try {
      const batchSize = Math.min(this.config.batchSizeLimit, this.logQueue.length);
      const batch = this.logQueue.splice(0, batchSize);

      if (batch.length === 0) return;

      await this.sendLogsToDynatrace(batch);
    } catch (error) {
      console.error('Failed to send batch to Dynatrace:', error);
    } finally {
      this.isSending = false;
    }
  }

  private async sendLogsToDynatrace(logs: DynatraceLogEntry[]): Promise<void> {
    try {
      const endpoint = `${this.config.url}${this.config.ingestEndpoint}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Api-Token ${this.config.apiToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(logs),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn(`Dynatrace log ingest failed. Status: ${response.status}, Error: ${errorText}`);
      }
    } catch (error) {
      console.error('Error sending logs to Dynatrace:', error);
    }
  }

  async flush(): Promise<void> {
    await this.sendBatch();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }

    // Send remaining logs
    if (this.config.enabled && this.logQueue.length > 0) {
      this.sendBatch().catch((err) => {
        console.error('Error flushing logs on dispose:', err);
      });
    }
  }
}

export class DynatraceLogger {
  private config: DynatraceConfig;
  private batchSender: DynatraceBatchSender | null = null;

  constructor(config: DynatraceConfig) {
    this.config = config;

    if (!config.enabled) return;

    try {
      this.batchSender = new DynatraceBatchSender(config);
    } catch (error) {
      console.error('Failed to initialize Dynatrace batch sender:', error);
    }
  }

  logTrace(message: string, attributes?: Record<string, any>): void {
    this.log('TRACE', message, attributes);
  }

  logDebug(message: string, attributes?: Record<string, any>): void {
    this.log('DEBUG', message, attributes);
  }

  logInfo(message: string, attributes?: Record<string, any>): void {
    this.log('INFO', message, attributes);
  }

  logWarning(message: string, attributes?: Record<string, any>): void {
    this.log('WARN', message, attributes);
  }

  logError(message: string, error?: unknown, attributes?: Record<string, any>): void {
    if (error) {
      this.logException('ERROR', message, error, attributes);
    } else {
      this.log('ERROR', message, attributes);
    }
  }

  logFatal(message: string, error?: unknown, attributes?: Record<string, any>): void {
    if (error) {
      this.logException('FATAL', message, error, attributes);
    } else {
      this.log('FATAL', message, attributes);
    }
  }

  logCustomEvent(eventName: string, message: string, attributes?: Record<string, any>): void {
    const customAttributes = { ...attributes, 'event.name': eventName };
    this.log('INFO', message, customAttributes);
  }

  private log(level: string, message: string, attributes?: Record<string, any>): void {
    if (!this.config.enabled || !message || !this.batchSender) return;

    try {
      const cleanAttributes = removeUndefinedValues(attributes);
      const logEntry: DynatraceLogEntry = {
        content: message,
        timestamp: new Date().toISOString(),
        'log.level': level,
        loglevel: level,
        status: level,
        'service.name': this.config.serviceName || 'unknown-service',
        ...(currentTraceId ? { 'trace.id': currentTraceId } : {}),
        ...cleanAttributes,
      };

      if (this.config.tags) {
        logEntry.tags = this.config.tags.split(',').map((t) => t.trim());
      }

      this.batchSender.enqueueLog(logEntry);
    } catch (error) {
      console.error('Failed to queue log entry for Dynatrace:', error);
    }
  }

  private logException(level: string, message: string, error: unknown, attributes?: Record<string, any>): void {
    if (!this.config.enabled || !error || !this.batchSender) return;

    try {
      const normalizedError = normalizeError(error);
      const cleanAttributes = removeUndefinedValues(attributes);
      const logEntry: DynatraceLogEntry = {
        content: message,
        timestamp: new Date().toISOString(),
        'log.level': level,
        loglevel: level,
        status: level,
        'service.name': this.config.serviceName || 'unknown-service',
        'exception.type': normalizedError.name,
        'exception.message': normalizedError.message,
        'exception.stacktrace': normalizedError.stack,
        ...(currentTraceId ? { 'trace.id': currentTraceId } : {}),
        ...cleanAttributes,
      };

      if (this.config.tags) {
        logEntry.tags = this.config.tags.split(',').map((t) => t.trim());
      }

      this.batchSender.enqueueLog(logEntry);
    } catch (err) {
      console.error('Failed to queue exception log entry for Dynatrace:', err);
    }
  }

  async flush(): Promise<void> {
    if (this.batchSender) {
      await this.batchSender.flush();
    }
  }

  dispose(): void {
    if (this.batchSender) {
      this.batchSender.dispose();
    }
  }
}

// Singleton instance
let dynatraceLoggerInstance: DynatraceLogger | null = null;

export function initializeDynatraceLogger(config: DynatraceConfig): DynatraceLogger {
  if (dynatraceLoggerInstance) {
    dynatraceLoggerInstance.dispose();
  }
  dynatraceLoggerInstance = new DynatraceLogger(config);
  return dynatraceLoggerInstance;
}

export function getDynatraceLogger(): DynatraceLogger | null {
  return dynatraceLoggerInstance;
}
